import { useLayoutEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api';
import { useAuth } from '../auth';
import { applyBusinessTheme, businessLogo } from '../businessTheme';
import { TeknikaMark, useSlugBrand } from '../components/Brand';
import { rememberBusiness } from '../lastBusiness';

/**
 * The contractor's sign-in: one column, big fields. Given a business (from its own
 * link, `/portal/login/:slug`, or the one this phone last signed in to; PortalApp
 * works out which) it wears that business's name, logo and colours and needs no
 * business code. Without one, it is Teknika CRM's generic fallback with the field.
 * `locked` is a business's own domain: there is no other business to choose, and
 * nothing to remember.
 */
export function PortalLogin(props: { slug?: string; locked?: boolean; onChooseOther(): void }) {
  const { slug } = props;
  const { signIn } = useAuth();
  const ownBrand = useSlugBrand(slug);
  const logo = ownBrand ? businessLogo(slug ?? '', ownBrand.logo) : '';
  // Before paint, so the default colours never flash ahead of the business's own.
  useLayoutEffect(() => {
    applyBusinessTheme(slug ?? '');
    return () => applyBusinessTheme('');
  }, [slug]);
  const [business, setBusiness] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    const chosen = (slug ?? business).trim();
    try {
      await signIn(chosen, email.trim(), password);
      if (!props.locked) rememberBusiness(chosen);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the server');
      setBusy(false);
    }
  }

  return (
    <form className="p-login" onSubmit={submit}>
      {logo ? (
        <img className="login-logo" src={logo} alt={ownBrand?.name} />
      ) : (
        <TeknikaMark size="2.9rem" />
      )}
      <h1>{ownBrand?.name ? `${ownBrand.name} contractor sign-in` : 'Contractor sign-in'}</h1>
      <p className="muted">Your visits, your week and extra work, on your phone.</p>
      {!slug && (
        <label>
          Business code
          <input
            value={business}
            onChange={(e) => setBusiness(e.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            aria-describedby="business-hint"
            required
          />
          <small id="business-hint" className="muted">
            The short name your office uses for its account. Ask the office if you do not know it.
            This phone remembers it after you sign in.
          </small>
        </label>
      )}
      <label>
        Email
        <input
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </label>
      <label>
        Password
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </label>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <button type="submit" className="p-big" disabled={busy}>
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
      <Link
        to={slug ? `/forgot-password?business=${encodeURIComponent(slug)}` : '/forgot-password'}
      >
        Forgot your password?
      </Link>
      {slug && !props.locked && (
        <button type="button" className="link" onClick={props.onChooseOther}>
          {ownBrand?.name
            ? `Not with ${ownBrand.name}? Use a different business`
            : 'Use a different business'}
        </button>
      )}
    </form>
  );
}
